import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  Lock,
  Users,
  Calendar,
  CreditCard,
  Settings,
  ShieldAlert,
  X,
  Bell,
  Mail
} from 'lucide-react';
import RenewalRequestModal from '../subscription/RenewalRequestModal';

interface SubscriptionCheckProps {
  children: React.ReactNode;
}

const SubscriptionCheck: React.FC<SubscriptionCheckProps> = ({ children }) => {
  const {
    currentUser,
    userRole,
    gymData,
    subscriptionStatus,
    isLimitReached,
    memberLimit,
    isSuperAdmin,
    loading,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [warningDismissed, setWarningDismissed] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);

  // Estados para verificación de correo
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [checking, setChecking] = useState(false);

  const handleResend = async () => {
    // Sin verificación de email en sistema MySQL
    setResendStatus('success');
  };

  const handleCheckVerification = async () => {
    // Sin verificación de email en sistema MySQL — recargar siempre
    window.location.reload();
  };

  // Si está cargando o es superadmin, no bloquear
  if (loading || isSuperAdmin) {
    return <>{children}</>;
  }

  // Si no hay usuario, mostrar hijos (el router se encargará de redirigir)
  if (!currentUser) {
    return <>{children}</>;
  }

  // Verificar si el email no está verificado para administradores/empleados del gimnasio
  const isGymStaff = userRole === 'admin' || userRole === 'user';
  const isEmailUnverified = currentUser && isGymStaff && !currentUser.emailVerified;

  if (isEmailUnverified) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[85vh] bg-gray-50/50 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-blue-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-10 text-white text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Mail size={120} />
            </div>
            <div className="bg-white/20 p-4 rounded-full w-18 h-18 flex items-center justify-center mx-auto mb-4 backdrop-blur-sm border border-white/30">
              <Mail size={32} className="text-white animate-pulse" />
            </div>
            <h2 className="text-2xl font-bold mb-1">Verificación de Correo</h2>
            <p className="text-blue-100 text-sm">Activa tu cuenta para comenzar</p>
          </div>

          <div className="p-6 space-y-6">
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-sm text-blue-800 space-y-2">
              <p>
                Hemos enviado un correo de verificación a:
              </p>
              <p className="font-bold text-center text-blue-900 text-base break-all bg-white p-2 rounded-lg border border-blue-100 shadow-sm">
                {currentUser.email}
              </p>
              <p className="text-xs text-blue-700 leading-relaxed pt-1">
                Por favor, haz clic en el enlace que recibiste para activar tu cuenta. Si no lo encuentras, revisa tu carpeta de <strong>Spam o Correo no deseado</strong>.
              </p>
            </div>

            {resendStatus === 'success' && (
              <div className="p-3 bg-green-50 border border-green-200 text-green-800 text-xs rounded-lg text-center font-medium shadow-sm">
                ✉️ Correo de verificación reenviado con éxito.
              </div>
            )}
            {resendStatus === 'error' && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-xs rounded-lg text-center font-medium shadow-sm">
                ❌ Error al reenviar el correo. Inténtalo más tarde.
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                onClick={handleCheckVerification}
                disabled={checking}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
              >
                {checking ? (
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></span>
                ) : null}
                Ya verifiqué mi correo
              </button>

              <button
                onClick={handleResend}
                disabled={resending}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 font-bold py-3 px-4 rounded-xl border-2 border-gray-200 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 shadow-sm"
              >
                {resending ? (
                  <span className="inline-block h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mr-1"></span>
                ) : null}
                Reenviar Correo de Verificación
              </button>

              <button
                onClick={async () => {
                  try {
                    logout();
                    window.location.href = '/login';
                  } catch (err) {
                    console.error('Error al cerrar sesión:', err);
                  }
                }}
                className="w-full bg-transparent hover:text-red-600 text-gray-450 font-semibold py-2 text-sm transition-colors text-center mt-2 hover:underline"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = subscriptionStatus === 'expired';
  const isSuspended = subscriptionStatus === 'suspended';
  const isBlocked = isExpired || isSuspended || isLimitReached;
  const isTrial = subscriptionStatus === 'trial';
  const showTrialBanner = isTrial && !isBlocked;

  // Calcular días restantes para el vencimiento
  let daysUntilExpiry: number | null = null;
  let isExpiringSoon = false;
  try {
    const endDate = (gymData as any)?.subscriptionData?.endDate;
    if (endDate) {
      const end = endDate.toDate ? endDate.toDate() : new Date(endDate);
      const now = new Date();
      daysUntilExpiry = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry <= 7;
    }
  } catch (e) { }

  // Solo admins ven el banner de aviso previo al vencimiento
  const showExpiryWarning =
    isExpiringSoon &&
    !isBlocked &&
    !warningDismissed &&
    userRole === 'admin';

  // Rutas permitidas durante el bloqueo
  const isAllowedPath =
    location.pathname.includes('/settings/business') ||
    location.pathname.includes('/help');

  if (isBlocked && !isAllowedPath) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[80vh] bg-gray-50/50 p-4 relative">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-red-100 overflow-hidden">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-red-600 to-red-500 px-8 py-10 text-white text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <ShieldAlert size={120} />
            </div>
            <div className="bg-white/20 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/30">
              <Lock size={40} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-2">Acceso Restringido</h2>
            <p className="text-red-100 text-lg">Tu cuenta requiere atención inmediata</p>
          </div>

          <div className="p-8">
            <div className="space-y-6">
              {/* Razón del bloqueo */}
              <div className="flex items-start gap-4 p-5 bg-red-50 rounded-xl border border-red-100">
                <div className="bg-red-100 p-2 rounded-lg text-red-600">
                  {isExpired ? <Calendar size={24} /> : isLimitReached ? <Users size={24} /> : <AlertTriangle size={24} />}
                </div>
                <div>
                  <h3 className="font-bold text-red-900 text-lg">
                    {isExpired ? 'Suscripción Vencida' :
                      isLimitReached ? 'Límite de Socios Superado' :
                        'Gimnasio Suspendido'}
                  </h3>
                  <p className="text-red-700 mt-1">
                    {isExpired ? 'Tu plan de suscripción ha llegado a su fin. Renovarlo es clave para seguir disfrutando del servicio.' :
                      isLimitReached ? `Has alcanzado el límite de ${memberLimit} socios permitido por tu plan actual.` :
                        'Tu gimnasio ha sido suspendido temporalmente. Contacta a soporte para más información.'}
                  </p>
                </div>
              </div>

              {/* Información Adicional */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <CreditCard size={18} />
                    <span className="text-sm font-medium">Plan Actual</span>
                  </div>
                  <p className="text-gray-900 font-bold">{(gymData as any)?.subscriptionData?.plan || 'Básico'}</p>
                </div>
                {memberLimit && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Users size={18} />
                      <span className="text-sm font-medium">Uso de Cupos</span>
                    </div>
                    <p className={`font-bold ${isLimitReached ? 'text-red-600' : 'text-gray-900'}`}>
                      {(gymData as any)?.totalMembers || 0} / {memberLimit}
                    </p>
                  </div>
                )}
              </div>

              {/* Acciones */}
              <div className="pt-4 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/settings/business')}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
                >
                  <CreditCard size={20} />
                  Renovar o Mejorar Plan
                </button>
                <button
                  onClick={() => navigate('/help')}
                  className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-bold py-4 px-6 rounded-xl border-2 border-gray-200 transition-all flex items-center justify-center gap-2"
                >
                  <Settings size={20} />
                  Contactar Soporte
                </button>
              </div>
            </div>

            <p className="text-center text-gray-400 text-xs mt-8 uppercase tracking-widest font-semibold">
              Desarrollado por Apoloservicios v12.0
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ⚠️ Banner de aviso de vencimiento próximo */}
      {showExpiryWarning && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-1.5 rounded-full flex-shrink-0">
                <Bell size={18} className="text-amber-600" />
              </div>
              <p className="text-amber-900 text-sm font-medium">
                {daysUntilExpiry === 0
                  ? '⚠️ Tu suscripción vence hoy'
                  : daysUntilExpiry === 1
                    ? '⚠️ Tu suscripción vence mañana'
                    : `⚠️ Tu suscripción vence en ${daysUntilExpiry} días`}
                <span className="text-amber-700 font-normal ml-1 hidden sm:inline">
                  — Renovar ahora para evitar interrupciones.
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setShowRenewalModal(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-4 py-1.5 rounded-lg transition-colors"
              >
                Renovar Ahora
              </button>
              <button
                onClick={() => setWarningDismissed(true)}
                className="text-amber-500 hover:text-amber-700 transition-colors"
                title="Cerrar aviso"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ Banner de Período de Prueba (Sin suscripción activa) */}
      {showTrialBanner && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2.5 shadow-md flex items-center justify-between gap-4 flex-wrap z-50">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-1.5 rounded-full flex-shrink-0">
              <AlertTriangle size={18} className="text-white animate-pulse" />
            </div>
            <p className="text-sm font-semibold tracking-wide">
              Sin suscripción activa — Sistema en Período de Prueba (Límite: {memberLimit || 10} socios activos)
            </p>
          </div>
          {userRole === 'admin' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowRenewalModal(true)}
                className="bg-white text-orange-600 hover:bg-orange-50 text-xs font-bold px-4 py-1.5 rounded-lg transition-all shadow-sm active:scale-95"
              >
                Adquirir Plan Activo
              </button>
            </div>
          )}
        </div>
      )}

      {children}

      {/* Modal de Renovación */}
      <RenewalRequestModal
        isOpen={showRenewalModal}
        onClose={() => setShowRenewalModal(false)}
        onSuccess={() => {
          setShowRenewalModal(false);
          setWarningDismissed(true);
        }}
      />
    </>
  );
};

export default SubscriptionCheck;
